Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
  const checkboxesToKeep = {};
  for (const element of formElement.elements) {
    const key = element.getAttribute('name');
    if (!key || !key.includes('filter')) continue;

    const value = element.value;
    const inputType = element.getAttribute('type');

    if (value === '') continue;

    if (inputType == 'number' && (element.hasAttribute('data-min') || element.hasAttribute('data-max'))) {
      const priceSlider = element.closest('price-range-slider');
      if (!priceSlider || !priceSlider.hasAttribute('data-updated')) {
        continue; // Skip if slider hasn't been updated by user
      }
    }

    if (inputType == 'checkbox') {
      if (element.checked) {
        if (!checkboxesToKeep[key]) {
          checkboxesToKeep[key] = [value];
          continue;
        } else {
          checkboxesToKeep[key].push(value);
          continue;
        }
      }

      if (Shopify.queryParams[key] && !checkboxesToKeep[key]) {
        delete Shopify.queryParams[key];
      }
      continue;
    } else {
      Shopify.queryParams[key] = value;
    }
  }

  if (Object.entries(checkboxesToKeep).length > 0) {
    for (const [key, value] of Object.entries(checkboxesToKeep)) {
      Shopify.queryParams[key] = value.join(',');
    }
  }

  return Shopify.queryParams;
};

/**
 * Syncs checkbox states between all facets-form instances
 * Ensures sidebar and drawer forms stay in sync
 * @param {HTMLElement} changedCheckbox - The checkbox that was changed (optional)
 */
const syncCheckboxStates = (changedCheckbox = null) => {
  const allFacetsForms = document.querySelectorAll('facets-form');

  // If a specific checkbox changed, sync only that one
  if (changedCheckbox) {
    const name = changedCheckbox.getAttribute('name');
    const value = changedCheckbox.value;
    const checked = changedCheckbox.checked;

    if (name && name.includes('filter')) {
      allFacetsForms.forEach(form => {
        const correspondingInput = form.querySelector(`input[name="${name}"][value="${value}"]`);
        if (correspondingInput && correspondingInput !== changedCheckbox) {
          correspondingInput.checked = checked;
        }
      });
    }
    return;
  }

  // Otherwise, sync all checkboxes
  const allCheckboxes = document.querySelectorAll('facets-form input[type="checkbox"]');
  allCheckboxes.forEach(checkbox => {
    const name = checkbox.getAttribute('name');
    const value = checkbox.value;

    if (!name || !name.includes('filter')) return;

    // Find all corresponding checkboxes in other forms
    allFacetsForms.forEach(form => {
      const correspondingInput = form.querySelector(`input[name="${name}"][value="${value}"]`);
      if (correspondingInput && correspondingInput !== checkbox) {
        correspondingInput.checked = checkbox.checked;
      }
    });
  });
};



/* --- LWC 19 August 2026 -------------------------------------------------- *
 * Taiga replaces the grid, the count, the active-filter row and the pagination
 * on a filter change, and nothing else, so every option count in the facet
 * markup still showed the pre-filter numbers and most of the options that were
 * left led to zero results. The two facet containers are replaced too now.
 *
 * They are two separate forms - the desktop rail is <form class="sidebar-facets">
 * inside .product-grid-sidebar, the drawer is <div id="facets"> inside
 * snippets/drawer-filters.liquid - but one fetched document carries both, and
 * each selector matches exactly one element in it, so the single existing
 * request supplies them both.
 *
 * Replacing that markup throws away four things the shopper owns: which
 * <details> groups are open, where the rail is scrolled to, the control they
 * are standing on, and the change listeners connectedCallback put on inputs
 * that no longer exist. All four are captured before the swap and put back
 * after it - see lwcCaptureFacetState for why focus has to be restored here and
 * not left to the timer in assets/lwc-ui.js.
 *
 * If a Taiga upgrade conflicts with this block, keep it.
 * ------------------------------------------------------------------------- */
const LWC_FACET_SELECTORS = ['form.sidebar-facets', '#facets'];

const lwcIsFacetSelector = (selector) => LWC_FACET_SELECTORS.indexOf(selector) !== -1;

/* The nearest ancestor that actually scrolls: .product-grid-sidebar for the
   rail, the drawer body for the drawer. */
const lwcScrollerOf = (el) => {
  let node = el;
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const lwcAttrEscape = (value) => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/* Groups are matched by their visible title, not by index: filtering can make a
   whole group disappear - Country collapses to one value and snippets/facets.liquid
   drops it - and an index would then reopen the wrong one. */
const lwcCaptureFacetState = (root) => {
  const open = {};
  root.querySelectorAll('details.filter-group').forEach((details) => {
    const label = details.querySelector('summary span');
    const key = label ? (label.textContent || '').trim() : '';
    if (key) open[key] = details.open;
  });

  /* The control the shopper is standing on is about to be destroyed by its own
     click. It has to be put back synchronously, in this same task: global.js's
     focus trap watches the open drawer with a MutationObserver, and a
     MutationObserver callback is a microtask, so if focus is still on <body>
     when it runs the trap sends the shopper back to the drawer's close button.
     Restoring here means the trap finds focus already inside its container and
     leaves it alone, and lwc-ui.js's own restoration - which runs on a 150ms
     timer and only acts when focus has been lost - stands down for the same
     reason. Its job is #current-facets-ajax, which is not this. */
  const active = document.activeElement;
  const focus = active && active !== document.body && root.contains(active) ? {
    key: active.getAttribute('data-key-value') || '',
    name: active.getAttribute('name') || '',
    value: active.getAttribute('value') || '',
    thumb: active.hasAttribute('data-thumb-min') ? 'min' : (active.hasAttribute('data-thumb-max') ? 'max' : '')
  } : null;

  const scroller = lwcScrollerOf(root);
  return { open, focus, scroller, scrollTop: scroller ? scroller.scrollTop : 0 };
};

const lwcRestoreFacetState = (root, state) => {
  if (!state) return;
  root.querySelectorAll('details.filter-group').forEach((details) => {
    const label = details.querySelector('summary span');
    const key = label ? (label.textContent || '').trim() : '';
    if (key in state.open) details.open = state.open[key];
    /* global.js drives <details> from a click handler that preventDefaults, so
       markup that arrives without an Accordion of its own would only toggle
       natively. These are all new elements, so there is nothing to double-bind. */
    if (typeof Accordion !== 'undefined') {
      try { new Accordion(details); } catch (err) {}
    }
  });

  if (state.focus) {
    const f = state.focus;
    let target = null;
    if (f.thumb) {
      target = root.querySelector(`[data-thumb-${ f.thumb }]`);
    } else if (f.key) {
      target = root.querySelector(`[data-key-value="${ lwcAttrEscape(f.key) }"]`);
    }
    if (!target && f.name) {
      target = root.querySelector(`[name="${ lwcAttrEscape(f.name) }"]${ f.value ? `[value="${ lwcAttrEscape(f.value) }"]` : '' }`);
    }
    /* preventScroll, or focusing a visually hidden checkbox scrolls the rail
       and undoes the offset restored on the next line. If the option has gone -
       filtering can remove a whole group - focus stays on <body> and lwc-ui.js
       picks it up and parks it on the results count, which is the right
       sentence to hear at that point. */
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (err) { try { target.focus(); } catch (err2) {} }
    }
  }

  if (state.scroller && state.scroller.isConnected) {
    state.scroller.scrollTop = state.scrollTop;
  }
};

/* The replaced inputs have no listeners. Re-bind, and tell assets/lwc-ui.js to
   put its type-to-filter boxes and slider labelling back. */
const lwcAfterFacetReplace = () => {
  document.querySelectorAll('facets-form').forEach((form) => {
    if (typeof form.bindInputs === 'function') form.bindInputs();
  });
  syncCheckboxStates();
  document.dispatchEvent(new CustomEvent('lwc:facets-replaced'));
};

/* Back and Forward. Woolman.Utils.prepareQueryParams() only adds what is in
   location.search, it never drops what has gone, so the object has to be
   emptied first or the filters just walked back past are still in it. */
const lwcApplyStateFromURL = () => {
  if (!Woolman.Collection) return;
  Shopify.queryParams = {};
  Woolman.Utils.prepareQueryParams();
  lwcSyncSortControls();
  Woolman.Collection.classList.add('processing');
  getCollectionAJAX([
    '#product-grid-ajax',
    '#products-count-ajax',
    '#current-facets-ajax',
    '#pagination-ajax'
  ].concat(LWC_FACET_SELECTORS), 'none');
};

/* Checkbox and price state come back with the replaced facet markup, because
   the server renders them. Sort sits outside it and has to be set by hand. */
const lwcSyncSortControls = () => {
  const value = Shopify.queryParams['sort_by'] || '';
  document.querySelectorAll('sort-by').forEach((el) => {
    el.isSyncing = true;
    if (el.select) {
      const option = value && el.select.querySelector(`option[value="${value}"]`);
      el.select.value = option ? value : (el.select.querySelector('option[selected]') || el.select.options[0] || {}).value;
    } else if (el.radios && el.radios.length) {
      el.radios.forEach((radio) => {
        radio.checked = value ? radio.value === value : radio.defaultChecked;
      });
    }
    el.isSyncing = false;
  });
};

if (Woolman.Collection) {
  window.addEventListener('popstate', lwcApplyStateFromURL);
}
/* --- end LWC ------------------------------------------------------------- */

/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 * @param {String} historyMode - LWC: 'push' for a user-initiated filter or sort
 *   change so Back walks back through them, 'none' when replaying a popstate,
 *   anything else (the default) replaces, which is what URL normalisation wants.
 */
const getCollectionAJAX = async (selectors, historyMode = 'replace') => {
  if (historyMode !== 'none') Shopify.queryParams.page = 1;
  const CollectionURLSearchParams = getCollectionURLSearchParams(),
        url = location.pathname + '?' + CollectionURLSearchParams;

  if (selectors) {
    try {
      const res = await fetch(url);
      const body = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(body, 'text/html');

      let facetsReplaced = false;

      for (const selector of selectors) {
        if (!document.querySelector(selector) || !doc.querySelector(selector)) continue;

        // LWC: keep the open groups and the rail's scroll offset across the swap
        const target = document.querySelector(selector),
              facetState = lwcIsFacetSelector(selector) ? lwcCaptureFacetState(target) : null;

        target.innerHTML = doc.querySelector(selector).innerHTML;

        if (facetState) {
          lwcRestoreFacetState(target, facetState);
          facetsReplaced = true;
        }

        // Update apply number
        if (selector === '#product-grid-ajax') {
          let filteredCount = parseInt(doc.querySelector(selector).dataset.filteredCount, 10),
              context = doc.querySelector(selector).dataset.context,
              zeroResultsMsg = document.getElementById('facets-no-results-message'),
              resultsStringOne = window.themeStrings.product_count_simple_one,
              resultsStringOther = window.themeStrings.product_count_simple_other;

          if (context === 'search') {
            resultsStringOne = window.themeStrings.results_with_count_one;
            resultsStringOther = window.themeStrings.results_with_count_other;
          }

          if (filteredCount === 0) {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.remove('hide');
          } else if (filteredCount === 1) {
            document.getElementById('apply-count').innerHTML = resultsStringOne.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          } else {
            document.getElementById('apply-count').innerHTML = resultsStringOther.replace('[count]', filteredCount);
            zeroResultsMsg.classList.add('hide');
          }
        }
      }

      // LWC: re-bind the new inputs and re-apply the lwc-ui enhancements
      if (facetsReplaced) lwcAfterFacetReplace();

      if (historyMode === 'push') {
        window.history.pushState({ lwcFacets: url }, '', url);
      } else if (historyMode !== 'none') {
        window.history.replaceState('', '', url);
      }
      Woolman.Collection.classList.remove('processing');

    } catch (err) {
      location.search = CollectionURLSearchParams;
    }
  } else {
    location.search = CollectionURLSearchParams;
  }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @returns
 */
const getCollectionURLSearchParams = () => {
  return new URLSearchParams(Object.entries(Shopify.queryParams)).toString().replaceAll(/%2C/g, ',');
};

class SortBySelect extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.select = this.querySelector('select');
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.selectors = {
      grid: '#product-grid-ajax',
      pagination: '#pagination-ajax',
    };
    this.isSyncing = false; // Flag to prevent change events during sync

    if (Shopify.queryParams['sort_by']) {
      if (this.select) {
        this.select.value = Shopify.queryParams['sort_by'];
      } else if (this.radios.length > 0) {
        this.radios.forEach(radio => {
          if (radio.value === Shopify.queryParams['sort_by']) {
            radio.checked = true;
          }
        });
      }
    }

    if (this.select) {
      this.select.addEventListener('change', this.onSelectChange.bind(this));
    } else if (this.radios.length > 0) {
      this.radios.forEach(radio => {
        radio.addEventListener('change', this.onSelectChange.bind(this));
      });
    }
  }

  connectedCallback() {
    // Initial sync is handled by the constructor setting values from query params
  }

  /**
   * Syncs the value of this sort-by element to all other sort-by instances
   * @param {string} value - The value to sync to all instances
   */
  syncToAllInstances(value) {
    if (this.isSyncing) return; // Prevent infinite loops

    this.isSyncing = true;
    const allSortByElements = document.querySelectorAll('sort-by');

    allSortByElements.forEach((sortByElement) => {
      if (sortByElement === this) return; // Skip self

      sortByElement.isSyncing = true; // Set flag on target element

      // Handle select elements
      if (sortByElement.select) {
        if (sortByElement.select.value !== value) {
          sortByElement.select.value = value;
        }
      }
      // Handle radio buttons
      else if (sortByElement.radios.length > 0) {
        sortByElement.radios.forEach(radio => {
          radio.checked = (radio.value === value);
        });
      }

      sortByElement.isSyncing = false; // Clear flag on target element
    });

    this.isSyncing = false;
  }

  async onSelectChange(event) {
    // Don't trigger AJAX if this change was caused by syncing
    if (this.isSyncing) {
      return;
    }

    const value = event.target.value;

    // Sync to all other sort-by instances (without triggering their change events)
    this.syncToAllInstances(value);

    // Now trigger the AJAX update
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    Shopify.queryParams.sort_by = value;
    getCollectionAJAX(Object.values(this.selectors), 'push'); // LWC: user-initiated, so Back undoes it
  }
}

if (customElements.get('sort-by') === undefined) {
  customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this.querySelector('form');
    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
      // LWC: the facet markup itself, or every count on screen goes stale
      facetsSidebar: 'form.sidebar-facets',
      facetsDrawer: '#facets',
    };

    this.el.addEventListener('submit', this.onFormSubmit.bind(this));
  }

  connectedCallback() {
    this.bindInputs();
  }

  /**
   * Set up input change listeners and sync functionality.
   * LWC: called again after a facet replacement, because the inputs these
   * listeners were on have been thrown away. data-lwc-bound keeps it idempotent
   * so an input is never listened to twice.
   */
  bindInputs() {
    const inputs = this.el.querySelectorAll('input');

    inputs.forEach((input) => {
      if (input.dataset.lwcBound) return;
      /* The type-to-filter boxes assets/lwc-ui.js adds are inputs inside this
         form but are not filters: they are never submitted and a change on one
         must not fetch anything. They were never bound before, because they are
         added after connectedCallback has run; leave it that way. */
      if (input.hasAttribute('data-lwc-facet-search')) return;
      input.dataset.lwcBound = '1';

      /* LWC 19 August 2026. One debounce, two waits. 150ms is right for a
         checkbox, which is a single discrete click, and far too short for a
         number field: typing "1200" into the price box at an ordinary 220ms a
         keystroke fired seven filter requests, and the first of them asked for
         everything under $1 and flashed a near empty grid at the shopper. The
         price fields get a longer wait off the same debounce helper in
         global.js rather than a second mechanism of their own. The slider
         thumbs write their value through these same two inputs, so the arrow
         keys are covered by it too. */
      const isPriceInput = input.type === 'number'
        && (input.hasAttribute('data-min') || input.hasAttribute('data-max'));

      // Create debounced AJAX handler for this input
      const debouncedAJAX = debounce(this.onInputChange.bind(this), isPriceInput ? 500 : 150);

      input.addEventListener('change', (event) => {
        if (input.type === 'checkbox') {
          // Sync immediately on change, before AJAX
          syncCheckboxStates(input);
        }
        // Then trigger debounced AJAX update
        debouncedAJAX(event);
      });
    });
  }

  async onFormSubmit(event) {
    event.preventDefault();
    Woolman.Collection.classList.add('processing');
    Woolman.Utils.prepareQueryParams();
    generateSearchParamsFromFormData(this.el);

    Woolman.ModalsAndDrawers.closeModalOrDrawer(this.closest('[data-drawer]').getAttribute('id'));
    getCollectionAJAX(Object.values(this.selectors), 'push'); // LWC: user-initiated, so Back undoes it
  }

  async onInputChange(event) {
    Woolman.Collection.classList.add('processing');
    generateSearchParamsFromFormData(this.el);
    getCollectionAJAX(Object.values(this.selectors), 'push'); // LWC: user-initiated, so Back undoes it
  }
}

// Sync checkboxes when drawer opens
const drawerFilter = document.getElementById('drawer-filter');
if (drawerFilter) {
  const observer = new MutationObserver((mutations) => {
    if (drawerFilter.classList.contains('is-open')) {
      // Ensure query params are up to date
      Woolman.Utils.prepareQueryParams();

      // Sync all checkboxes from URL params
      const drawerForm = drawerFilter.querySelector('facets-form form');
      if (drawerForm) {
        const drawerCheckboxes = drawerForm.querySelectorAll('input[type="checkbox"]');
        drawerCheckboxes.forEach(checkbox => {
          const paramName = checkbox.getAttribute('name');
          const value = checkbox.value;

          if (paramName && paramName.includes('filter') && Shopify.queryParams[paramName]) {
            const paramValues = Shopify.queryParams[paramName].split(',');
            checkbox.checked = paramValues.includes(value);
          }
        });
      }

      // Sync all forms to ensure consistency
      syncCheckboxStates();
    }
  });

  observer.observe(drawerFilter, {
    attributes: true,
    attributeFilter: ['class']
  });
}

if (customElements.get('facets-form') === undefined) {
  customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.selectors = {
      grid: '#product-grid-ajax',
      count: '#products-count-ajax',
      currentFacets: '#current-facets-ajax',
      pagination: '#pagination-ajax',
      // LWC: the facet markup itself, or every count on screen goes stale
      facetsSidebar: 'form.sidebar-facets',
      facetsDrawer: '#facets',
    };

    this.btn = this.querySelector('a');
    this.btn.addEventListener('click', this.removeFacet.bind(this));
  }

  async removeFacet(event) {
    event.preventDefault();
    if (event.currentTarget.dataset.filterParamName === 'filter.v.price') {
      // Delete price filter params
      delete Shopify.queryParams['filter.v.price.lte'];
      delete Shopify.queryParams['filter.v.price.gte'];

      // Reset all price range sliders (sidebar and drawer)
      if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
        PriceRangeSlider.resetAll();
      }

      // Remove data-updated attribute from all sliders
      const allSliders = document.querySelectorAll('price-range-slider');
      allSliders.forEach(slider => {
        slider.removeAttribute('data-updated');
      });

      getCollectionAJAX(Object.values(this.selectors), 'push'); // LWC: user-initiated, so Back undoes it
    } else {
      const inputToReset = document.querySelector(`.filter-form [data-key-value="${ event.currentTarget.dataset.filterParamName }:${ event.currentTarget.dataset.filterValue }"]`);
      inputToReset.checked = false;
      inputToReset.dispatchEvent(new Event('change'));
    }
  }
}
if (customElements.get('current-facet-button') === undefined) {
  customElements.define('current-facet-button', CurrentFacetButton);
}